import { useEffect, useState } from "react";
import { Users, Plus, Trash2, Wallet, ArrowRight } from "lucide-react";
import { api, money, trDate, todayISO } from "../lib/api";
import { PageHeader, Modal, FormField, SubmitButton, useForm, EmptyState, StatMini } from "../components/farm/FarmUI";
import { HERO, HERO_TINT } from "../lib/heroes";

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState("");
  const [empOpen, setEmpOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const empForm = useForm({ name: "", monthly_salary: "", phone: "", start_date: "", note: "" });
  const payForm = useForm({ employee_id: "", type: "salary", amount: "", date: todayISO(), note: "" });

  const load = async () => {
    try {
      const [e, p] = await Promise.all([api.get("/employees"), api.get("/employee-payments")]);
      setEmployees(e); setPayments(p);
    } catch { setError("Yüklenemedi."); }
  };
  useEffect(() => { load(); }, []);

  const totalSalary = employees.reduce((a, e) => a + (Number(e.monthly_salary) || 0), 0);
  const totalPaid = payments.filter((p) => p.type === "salary").reduce((a, p) => a + (Number(p.amount) || 0), 0);
  const totalAdvance = payments.filter((p) => p.type === "advance").reduce((a, p) => a + (Number(p.amount) || 0), 0);

  const submitEmp = async (e) => {
    e.preventDefault();
    const payload = {
      name: empForm.form.name,
      monthly_salary: Number(empForm.form.monthly_salary) || 0,
      phone: empForm.form.phone,
      start_date: empForm.form.start_date || null,
      note: empForm.form.note,
    };
    try {
      const rec = await api.post("/employees", payload);
      setEmployees((x) => [rec, ...x]);
      empForm.reset();
      setEmpOpen(false);
    } catch { setError("Eklenemedi."); }
  };

  const submitPay = async (e) => {
    e.preventDefault();
    try {
      const rec = await api.post("/employee-payments", {
        employee_id: payForm.form.employee_id,
        type: payForm.form.type,
        amount: Number(payForm.form.amount) || 0,
        date: payForm.form.date,
        note: payForm.form.note,
      });
      setPayments((x) => [rec, ...x]);
      payForm.reset();
      setPayOpen(false);
      setPayFor(null);
    } catch { setError("Ödeme kaydedilemedi."); }
  };

  const openPayFor = (emp) => {
    setPayFor(emp);
    payForm.setForm({ employee_id: emp.id, type: "salary", amount: emp.monthly_salary || "", date: todayISO(), note: "" });
    setPayOpen(true);
  };

  const removeEmp = async (id) => {
    if (!window.confirm("Çalışan ve tüm ödeme kayıtları silinecek. Emin misiniz?")) return;
    try {
      await api.del(`/employees/${id}`);
      setEmployees((x) => x.filter((i) => i.id !== id));
      setPayments((x) => x.filter((p) => p.employee_id !== id));
    } catch { setError("Silinemedi."); }
  };

  const removePay = async (id) => {
    try { await api.del(`/employee-payments/${id}`); setPayments((x) => x.filter((i) => i.id !== id)); }
    catch { setError("Silinemedi."); }
  };

  return (
    <>
      <PageHeader
        eyebrow="ÇALIŞANLAR · MAAŞ TAKİBİ"
        title="Çalışanlar"
        subtitle="Maaşlı çalışanlarınız, ödemeler ve avanslar."
        heroImage={HERO.employees}
        heroTint={HERO_TINT.employees}
        actions={
          <button className="primary-btn" data-testid="emp-add-btn" onClick={() => setEmpOpen(true)}>
            <Plus size={17} /> Çalışan ekle
          </button>
        }
      />

      {error && <div className="api-error" data-testid="emp-error">{error}</div>}

      <section className="stats-mini-row">
        <StatMini label="Çalışan sayısı" value={`${employees.length}`} tone="teal" testId="emp-stat-count" />
        <StatMini label="Aylık toplam maaş" value={money(totalSalary)} tone="orange" testId="emp-stat-salary" />
        <StatMini label="Ödenmiş maaş" value={money(totalPaid)} tone="green" testId="emp-stat-paid" />
        <StatMini label="Avans" value={money(totalAdvance)} tone="red" testId="emp-stat-advance" />
      </section>

      <div className="panel">
        {employees.length === 0 ? (
          <EmptyState icon={Users} title="Henüz çalışan yok" text="Maaşlı çalışanlarınızı ekleyerek ödeme ve avans kayıtlarını tutabilirsiniz." actionLabel="İlk çalışanı ekle" onAction={() => setEmpOpen(true)} testId="emp-empty-add" />
        ) : (
          <div className="emp-grid">
            {employees.map((e) => {
              const empPays = payments.filter((p) => p.employee_id === e.id);
              const paid = empPays.filter((p) => p.type === "salary").reduce((a, p) => a + Number(p.amount || 0), 0);
              const adv = empPays.filter((p) => p.type === "advance").reduce((a, p) => a + Number(p.amount || 0), 0);
              return (
                <div className="emp-card" key={e.id} data-testid={`emp-item-${e.id}`}>
                  <div className="emp-head">
                    <div className="emp-avatar">{e.name?.charAt(0)?.toUpperCase() || "?"}</div>
                    <div className="emp-info">
                      <b>{e.name}</b>
                      <small>{e.phone || "-"} · {e.start_date ? `Başlangıç: ${trDate(e.start_date)}` : "Başlangıç yok"}</small>
                    </div>
                    <button className="delete-btn always" data-testid={`emp-delete-${e.id}`} onClick={() => removeEmp(e.id)}><Trash2 size={14} /></button>
                  </div>
                  <div className="emp-metrics">
                    <div><span>Maaş</span><b>{money(e.monthly_salary)}</b></div>
                    <div><span>Ödenen</span><b className="positive">{money(paid)}</b></div>
                    <div><span>Avans</span><b className="negative">{money(adv)}</b></div>
                  </div>
                  <button className="ghost-btn" data-testid={`emp-pay-btn-${e.id}`} onClick={() => openPayFor(e)}>
                    <Wallet size={15} /> Ödeme / Avans kaydet <ArrowRight size={14} />
                  </button>
                  {empPays.length > 0 && (
                    <div className="emp-payments">
                      {empPays.slice(0, 3).map((p) => (
                        <div className="emp-pay-row" key={p.id} data-testid={`pay-item-${p.id}`}>
                          <span className={`emp-pay-tag ${p.type}`}>{p.type === "salary" ? "Maaş" : "Avans"}</span>
                          <b>{money(p.amount)}</b>
                          <small>{trDate(p.date)}</small>
                          <button className="delete-btn always" data-testid={`pay-delete-${p.id}`} onClick={() => removePay(p.id)}><Trash2 size={12} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {empOpen && (
        <Modal title="Yeni çalışan" subtitle="ÇALIŞAN" onClose={() => setEmpOpen(false)}>
          <form onSubmit={submitEmp}>
            <FormField label="Ad Soyad">
              <input required data-testid="emp-form-name" value={empForm.form.name} onChange={(e) => empForm.set("name", e.target.value)} />
            </FormField>
            <div className="form-row">
              <FormField label="Aylık maaş (₺)">
                <input required type="number" step="1" data-testid="emp-form-salary" value={empForm.form.monthly_salary} onChange={(e) => empForm.set("monthly_salary", e.target.value)} />
              </FormField>
              <FormField label="Telefon">
                <input data-testid="emp-form-phone" value={empForm.form.phone} onChange={(e) => empForm.set("phone", e.target.value)} />
              </FormField>
            </div>
            <FormField label="Başlangıç tarihi">
              <input type="date" data-testid="emp-form-start" value={empForm.form.start_date} onChange={(e) => empForm.set("start_date", e.target.value)} />
            </FormField>
            <FormField label="Not">
              <textarea data-testid="emp-form-note" value={empForm.form.note} onChange={(e) => empForm.set("note", e.target.value)} />
            </FormField>
            <SubmitButton testId="emp-form-submit">Çalışanı kaydet</SubmitButton>
          </form>
        </Modal>
      )}

      {payOpen && (
        <Modal title={`Ödeme: ${payFor?.name || ""}`} subtitle="MAAŞ / AVANS" onClose={() => { setPayOpen(false); setPayFor(null); }}>
          <form onSubmit={submitPay}>
            <FormField label="Ödeme türü">
              <select required data-testid="pay-form-type" value={payForm.form.type} onChange={(e) => payForm.set("type", e.target.value)}>
                <option value="salary">Maaş ödemesi</option>
                <option value="advance">Avans</option>
              </select>
            </FormField>
            <div className="form-row">
              <FormField label="Tutar (₺)">
                <input required type="number" step="1" data-testid="pay-form-amount" value={payForm.form.amount} onChange={(e) => payForm.set("amount", e.target.value)} />
              </FormField>
              <FormField label="Tarih">
                <input required type="date" data-testid="pay-form-date" value={payForm.form.date} onChange={(e) => payForm.set("date", e.target.value)} />
              </FormField>
            </div>
            <FormField label="Not">
              <textarea data-testid="pay-form-note" value={payForm.form.note} onChange={(e) => payForm.set("note", e.target.value)} />
            </FormField>
            <SubmitButton testId="pay-form-submit">Ödemeyi kaydet</SubmitButton>
          </form>
        </Modal>
      )}
    </>
  );
}
